import React, { memo, useMemo, useRef } from "react";
import { useImmer } from "use-immer";

import { IgAnalyticsTooltip, IgToggleButtonGroup } from "@intelligems/ig-theme";
import {
  AnalyzeTinybirdResourceResponseBodyType,
  CustomEventDto,
} from "@intelligems/ig-types";
import { GraphConfig, GraphType } from "@intelligems/ig-types";
import { ToggleButton } from "@mui/material";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { selectData } from "@src/api/analytics/dashboard-tinybird";
import { useGetTinybirdExperienceAnalysis } from "@src/api/analytics/get-tinybird-experience-analysis";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { BaseMetricSection } from "@src/features/analytics/baseDashboard/BaseMetricSection";
import { BaseGraphPaper } from "@src/features/analytics/baseDashboard/chartComponents/GraphPapers";
import { GraphsWrapper } from "@src/features/analytics/baseDashboard/chartComponents/GraphsWrapper";
import { InsufficientCogsAlert } from "@src/features/analytics/baseDashboard/InsufficientCogsAlert";
import { NoCogsGraphPlaceholder } from "@src/features/analytics/baseDashboard/NoCogsGraphPlaceholder";
import { VariationHeaderDropdown } from "@src/features/analytics/baseDashboard/VariationHeaderDropdown";
import { useSetGridWidthAnalytics } from "@src/features/analytics/hooks/use-set-grid-width-analytics";
import { MetricDetailsWrapper } from "@src/features/analytics/metricDetails/MetricDetailsWrapper";
import { AnalyticsAudienceTableWrapper } from "@src/features/analytics/tables/analyticsAudienceTable/AnalyticsAudienceTableWrapper";
import {
  getGridWidthCalc,
  shouldShowInsufficientCogsWarning,
  shouldShowProfitData,
} from "@src/features/analytics/utils";
import { useSelector } from "@src/store";
import { getExperienceAnalyticsFilters } from "@src/store/slices/dashboardFilters";
import { selectedExperience } from "@src/store/slices/experiences";
import { selectedVariations } from "@src/store/slices/variations";
import { getControlVariationId } from "@src/utils/controlVariation";
import { UseQueryResult } from "@tanstack/react-query";

const histogramConfig: GraphConfig[] = [
  {
    title: [""],
    filterMetric: "variation_id",
    metrics: ["total_units"],
    dataTypes: ["currencyNoDecimal"],
    datasetId: "variation_histogram",
    graphType: GraphType.Histogram,
    canBeKeyMetric: false,
    hasProbability: false,
    hasValueCI: false,
    hasAudience: false,
    hasTimeseries: false,
    histogramBucket: "bin",
  },
];
interface DiscountAnalyticsPageProps {
  data: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  totalExperienceVisitors: number;
  graphConfigs: GraphConfig[];
  testDuration: number;
  compareVariationId: string;
}
export const DiscountAnalyticsPage = memo(
  ({
    data,
    totalExperienceVisitors,
    graphConfigs,
    testDuration,
    compareVariationId,
  }: DiscountAnalyticsPageProps) => {
    const experience = useSelector(selectedExperience);
    const variations = useSelector(selectedVariations);
    const dashboardFilters = useSelector(getExperienceAnalyticsFilters);
    const storeCurrencyConfig = useConfiguration<{
      code: string;
      locale: string;
    }>("shop_currency");
    const shouldShowProfit = useMemo(() => {
      return shouldShowProfitData(data[0]?.data?.metrics || []);
    }, [data]);
    const [selectedVariationId, setSelectedVariationId] = useImmer(
      dashboardFilters?.compareGroupB || getControlVariationId(variations)
    );

    const [hover, setHover] = useImmer(-1);
    const [metricFilterIndex, setMetricFilterIndex] = useImmer(0);
    const histogramDataQuery = useGetTinybirdExperienceAnalysis(
      {
        graphConfigs: histogramConfig,
        experience,
        histogramValues: {
          histogramMetric: "total_discount",
          kind: "histogram",
        },
        overviewDataLoaded: data[0].isSuccess,
      },
      "experiences"
    );

    const isDataLoading = useMemo(() => {
      return data[0].isLoading || data[0].isPending;
    }, [data[0].isLoading, data[0].isPending]);

    const ref = useRef<HTMLDivElement | null>(null);
    const gridWidth = useSetGridWidthAnalytics(ref);

    return (
      <Stack ref={ref} direction="column" spacing={4}>
        {shouldShowInsufficientCogsWarning(data) && (
          <Grid>
            <InsufficientCogsAlert />
          </Grid>
        )}
        <Grid>
          <BaseMetricSection
            groupTitle={"Discount Metrics"}
            showCompareGroups={true}
            rightSideButtons={[
              <IgAnalyticsTooltip
                key={"filter-buttons"}
                title={"Choose the scope of these metrics"}
              >
                <IgToggleButtonGroup
                  disabled={data[0].isLoading}
                  buttons={[
                    <ToggleButton
                      value="discount"
                      key="discount"
                      size={"small"}
                      sx={{
                        borderRadius: "30px",
                        paddingLeft: "12px",
                      }}
                    >
                      Discounted Orders Only
                    </ToggleButton>,
                    <ToggleButton
                      value="all"
                      key="all"
                      size={"small"}
                      sx={{
                        borderRadius: "30px",
                        paddingRight: "12px",
                      }}
                    >
                      All Orders
                    </ToggleButton>,
                  ]}
                  value={metricFilterIndex === 0 ? "discount" : "all"}
                  control={{
                    exclusive: true,
                    onChange: (event, value: string) => {
                      if (value) {
                        setMetricFilterIndex(value === "discount" ? 0 : 1);
                      }
                    },
                  }}
                />
              </IgAnalyticsTooltip>,
            ]}
          >
            <Grid size={gridWidth}>
              <BaseGraphPaper
                onMouseEnter={() => {
                  setHover(0);
                }}
                onMouseLeave={() => {
                  setHover(-1);
                }}
              >
                <GraphsWrapper
                  testDuration={testDuration}
                  totalTestVisitors={totalExperienceVisitors}
                  config={graphConfigs[0]}
                  renderLocation={"experiment"}
                  isDataLoading={isDataLoading}
                  data={selectData({
                    dashboardData: data,
                    config: graphConfigs[0],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex: 0,
                    variations,
                  })}
                  compareVariationId={compareVariationId}
                  rawData={data}
                />
              </BaseGraphPaper>
            </Grid>
            <Grid size={gridWidth}>
              <BaseGraphPaper
                onMouseEnter={() => {
                  setHover(1);
                }}
                onMouseLeave={() => {
                  setHover(-1);
                }}
              >
                <GraphsWrapper
                  config={graphConfigs[1]}
                  testDuration={testDuration}
                  isDataLoading={isDataLoading}
                  metricFilterIndex={metricFilterIndex}
                  totalTestVisitors={totalExperienceVisitors}
                  renderLocation={"experiment"}
                  data={selectData({
                    dashboardData: data,
                    config: graphConfigs[1],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex,
                    variations,
                  })}
                  compareVariationId={compareVariationId}
                  rawData={data}
                />
              </BaseGraphPaper>
            </Grid>
            <Grid size={gridWidth}>
              <BaseGraphPaper
                onMouseEnter={() => {
                  setHover(2);
                }}
                onMouseLeave={() => {
                  setHover(-1);
                }}
              >
                <GraphsWrapper
                  config={graphConfigs[2]}
                  testDuration={testDuration}
                  renderLocation={"experiment"}
                  isDataLoading={isDataLoading}
                  metricFilterIndex={metricFilterIndex}
                  totalTestVisitors={totalExperienceVisitors}
                  data={selectData({
                    dashboardData: data,
                    config: graphConfigs[2],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex,
                    variations,
                  })}
                  compareVariationId={compareVariationId}
                  rawData={data}
                />
              </BaseGraphPaper>
            </Grid>
            <Grid size={gridWidth}>
              {!shouldShowProfit ? (
                <NoCogsGraphPlaceholder config={graphConfigs[3]} />
              ) : (
                <BaseGraphPaper
                  onMouseEnter={() => {
                    setHover(3);
                  }}
                  onMouseLeave={() => {
                    setHover(-1);
                  }}
                >
                  <GraphsWrapper
                    config={graphConfigs[3]}
                    renderLocation={"experiment"}
                    testDuration={testDuration}
                    isDataLoading={isDataLoading}
                    metricFilterIndex={metricFilterIndex}
                    totalTestVisitors={totalExperienceVisitors}
                    data={selectData({
                      dashboardData: data,
                      config: graphConfigs[3],
                      _resource: experience,
                      locale: storeCurrencyConfig?.value?.locale,
                      metricFilterIndex,
                      variations,
                    })}
                    compareVariationId={compareVariationId}
                    rawData={data}
                  />
                </BaseGraphPaper>
              )}
            </Grid>
          </BaseMetricSection>
        </Grid>
        <Grid size={12}>
          <BaseMetricSection
            tooltip={"Distribution of orders by total discount."}
            headerDropdown={
              <VariationHeaderDropdown
                selectedVariationId={selectedVariationId}
                setSelectedVariationId={setSelectedVariationId}
              />
            }
            groupTitle={"Discount Histogram"}
          >
            <Grid size={12}>
              <BaseGraphPaper>
                <GraphsWrapper
                  testDuration={testDuration}
                  focusedVariationId={selectedVariationId}
                  config={histogramConfig[0]}
                  isDataLoading={
                    histogramDataQuery[0].isLoading ||
                    histogramDataQuery[0].isPending
                  }
                  totalTestVisitors={totalExperienceVisitors}
                  renderLocation={"experiment"}
                  data={selectData({
                    dashboardData: histogramDataQuery,
                    config: histogramConfig[0],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex: 0,
                    variations,
                  })}
                  compareVariationId={compareVariationId}
                  rawData={data}
                />
              </BaseGraphPaper>
            </Grid>
          </BaseMetricSection>
        </Grid>
        <Grid>
          <MetricDetailsWrapper
            graphConfigs={graphConfigs}
            dropdownOptions={graphConfigs.slice(0, 5).map((config) => ({
              title: config.title[0],
              id: config.metrics[
                config.hasMetricFilterIndex ? metricFilterIndex : 0
              ],
            }))}
            variationOverviewData={data}
            metricFilterIndex={metricFilterIndex}
          />
        </Grid>
        <Grid>
          <AnalyticsAudienceTableWrapper
            experience={experience}
            variations={variations}
            dashboardData={data}
            graphConfigs={graphConfigs.filter((config) => config.hasAudience)}
            testDuration={testDuration}
            metricFilterIndex={metricFilterIndex}
          />
        </Grid>
      </Stack>
    );
  }
);
